import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { storage } from '../utils/storage';
import { User, UserRole } from '../types';
import { Plus, Search, UserX, UserCheck, Edit2, Trash2 } from 'lucide-react';
export function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'CHAUFFEUR' as UserRole
  });
  useEffect(() => {
    loadUsers();
  }, []);
  const loadUsers = () => {
    setUsers(storage.getUsers());
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newUser: User = {
      id: editingUser ? editingUser.id : `u${Date.now()}`,
      name: formData.name,
      email: formData.email,
      role: formData.role,
      password: formData.password || (editingUser ? editingUser.password : '123456'),
      active: editingUser ? editingUser.active : true
    };
    storage.saveUser(newUser);
    loadUsers();
    resetForm();
  };
  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });
    setIsFormOpen(true);
  };
  const toggleStatus = (user: User) => {
    const updatedUser = {
      ...user,
      active: !user.active
    };
    storage.saveUser(updatedUser);
    loadUsers();
  };
  const resetForm = () => {
    setIsFormOpen(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'CHAUFFEUR'
    });
  };
  const filteredUsers = users.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()) || u.email.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div className="space-y-6" data-id="element-737">
      <div className="flex justify-between items-center" data-id="element-738">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-739">
          Gestion Utilisateurs
        </h1>
        <Button onClick={() => setIsFormOpen(true)} leftIcon={<Plus className="w-4 h-4" data-id="element-741" />} data-id="element-740">
          Nouveau Utilisateur
        </Button>
      </div>

      {/* Create/Edit Form Modal (Inline for simplicity) */}
      {isFormOpen && <Card className="bg-blue-50 border-blue-100" data-id="element-742">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-743">
            <h3 className="font-bold text-lg" data-id="element-744">
              {editingUser ? 'Modifier Utilisateur' : 'Créer Utilisateur'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-745">
              <Input label="Nom complet" name="name" value={formData.name} onChange={handleInputChange} required data-id="element-746" />
              <Input label="Email" name="email" type="email" value={formData.email} onChange={handleInputChange} required data-id="element-747" />
              <Input label={editingUser ? 'Nouveau mot de passe (laisser vide pour garder)' : 'Mot de passe'} name="password" type="password" value={formData.password} onChange={handleInputChange} required={!editingUser} data-id="element-748" />
              <Select label="Rôle" name="role" value={formData.role} onChange={handleInputChange} options={[{
            value: 'ADMIN',
            label: 'Administrateur'
          }, {
            value: 'CHAUFFEUR',
            label: 'Chauffeur'
          }, {
            value: 'DEPOT',
            label: 'Agent Dépôt'
          }]} data-id="element-749" />
            </div>
            <div className="flex gap-2 justify-end" data-id="element-750">
              <Button type="button" variant="secondary" onClick={resetForm} data-id="element-751">
                Annuler
              </Button>
              <Button type="submit" data-id="element-752">
                {editingUser ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </Card>}

      <Card data-id="element-753">
        <div className="mb-4 relative" data-id="element-754">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-755" />
          <Input placeholder="Rechercher un utilisateur..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-756" />
        </div>

        <div className="overflow-x-auto" data-id="element-757">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-758">
            <thead className="bg-gray-50" data-id="element-759">
              <tr data-id="element-760">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-761">
                  Nom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-762">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-763">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-764">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-765">
              {filteredUsers.map(user => <tr key={user.id} data-id="element-766">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-767">
                    <div className="font-medium text-gray-900" data-id="element-768">{user.name}</div>
                    <div className="text-sm text-gray-500" data-id="element-769">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-770">
                    <span className={`px-2 py-1 text-xs font-bold rounded ${user.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' : user.role === 'CHAUFFEUR' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`} data-id="element-771">
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-772">
                    {user.active ? <span className="text-green-600 flex items-center text-sm" data-id="element-773">
                        <UserCheck className="w-4 h-4 mr-1" data-id="element-774" /> Actif
                      </span> : <span className="text-red-600 flex items-center text-sm" data-id="element-775">
                        <UserX className="w-4 h-4 mr-1" data-id="element-776" /> Inactif
                      </span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-777">
                    <button onClick={() => handleEdit(user)} className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-778">
                      <Edit2 className="w-4 h-4" data-id="element-779" />
                    </button>
                    <button onClick={() => toggleStatus(user)} className={`${user.active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`} data-id="element-780">
                      {user.active ? <Trash2 className="w-4 h-4" data-id="element-781" /> : <UserCheck className="w-4 h-4" data-id="element-782" />}
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}